import React, { createContext, useContext, useReducer, useMemo, useEffect } from 'react';
import { useOcelContext } from '../OcelContext';
import { useTabFilterOverride } from './TabFilterOverrideContext';

// Updated state structure for filters only (no file management)
const initialFilterState = {
  active: {
    objectTypes: [],
    dateRange: { startDate: null, endDate: null, enabled: false },
    activityFilters: { included: [], excluded: [], enabled: false },
    leadTimeRange: { min: null, max: null, enabled: false },
    objectAttributePresenceFilters: { attributeNames: [], enabled: false },
    eventAttributeFilters: [],
    eventAttributeFilterSettings: { matchType: 'all' },
    objectAttributeFilters: [],
    objectAttributeFilterSettings: { mode: 'include', matchType: 'all' },
    variantFilter: { objectType: null, variantActivitiesList: null, enabled: false, displayMode: 'fade' },
    edgeDurationFilters: [],
    edgeContainmentFilters: { enabled: false, edges: [], matchType: 'any', mode: 'include' },
    activityNodeFilters: { enabled: false, nodes: [], matchType: 'any', mode: 'include' },
  },
  draft: {
    objectTypes: [],
    dateRange: { startDate: null, endDate: null, enabled: false },
    activityFilters: { included: [], excluded: [], enabled: false },
    leadTimeRange: { min: null, max: null, enabled: false },
    objectAttributePresenceFilters: { attributeNames: [], enabled: false },
    eventAttributeFilters: [],
    eventAttributeFilterSettings: { matchType: 'all' },
    objectAttributeFilters: [],
    objectAttributeFilterSettings: { mode: 'include', matchType: 'all' },
    variantFilter: { objectType: null, variantActivitiesList: null, enabled: false, displayMode: 'fade' },
    edgeDurationFilters: [],
    edgeContainmentFilters: { enabled: false, edges: [], matchType: 'any', mode: 'include' },
    activityNodeFilters: { enabled: false, nodes: [], matchType: 'any', mode: 'include' },
  },
  filterPanelOpen: false,
  edgeSelectMode: false,
  edgeDurationMode: false,
};

const filterReducer = (state, action) => {
  switch (action.type) {

    case 'TOGGLE_DRAFT_OBJECT_TYPE':
      const { objectType } = action.payload;
      const isSelected = state.draft.objectTypes.includes(objectType);
      return {
        ...state,
        draft: {
          ...state.draft,
          objectTypes: isSelected
            ? state.draft.objectTypes.filter(type => type !== objectType)
            : [...state.draft.objectTypes, objectType],
        },
      };

    case 'SET_DRAFT_DATE_RANGE':
      return {
        ...state,
        draft: { ...state.draft, dateRange: { ...state.draft.dateRange, ...action.payload } },
      };

    case 'ADD_DRAFT_EVENT_ATTRIBUTE_FILTER':
      console.log('🔧 ADD_DRAFT_EVENT_ATTRIBUTE_FILTER reducer:', action.payload);
      const newEventState = {
        ...state,
        draft: { ...state.draft, eventAttributeFilters: [...state.draft.eventAttributeFilters, action.payload] },
      };
      console.log('🔧 New draft eventAttributeFilters:', newEventState.draft.eventAttributeFilters);
      return newEventState;

    case 'REMOVE_DRAFT_EVENT_ATTRIBUTE_FILTER':
      return {
        ...state,
        draft: {
          ...state.draft,
          eventAttributeFilters: state.draft.eventAttributeFilters.filter((_, index) => index !== action.payload),
        },
      };

    case 'UPDATE_DRAFT_EVENT_ATTRIBUTE_FILTER':
      const { index, updates } = action.payload;
      return {
        ...state,
        draft: {
          ...state.draft,
          eventAttributeFilters: state.draft.eventAttributeFilters.map((filter, i) =>
            i === index ? { ...filter, ...updates } : filter
          ),
        },
      };

    case 'ADD_DRAFT_OBJECT_ATTRIBUTE_FILTER':
      console.log('🔧 ADD_DRAFT_OBJECT_ATTRIBUTE_FILTER reducer:', action.payload);
      const newObjectState = {
        ...state,
        draft: { ...state.draft, objectAttributeFilters: [...state.draft.objectAttributeFilters, action.payload] },
      };
      console.log('🔧 New draft objectAttributeFilters:', newObjectState.draft.objectAttributeFilters);
      return newObjectState;

    case 'REMOVE_DRAFT_OBJECT_ATTRIBUTE_FILTER':
      return {
        ...state,
        draft: {
          ...state.draft,
          objectAttributeFilters: state.draft.objectAttributeFilters.filter((_, index) => index !== action.payload),
        },
      };

    case 'UPDATE_DRAFT_OBJECT_ATTRIBUTE_FILTER':
      const { index: objIndex, updates: objUpdates } = action.payload;
      return {
        ...state,
        draft: {
          ...state.draft,
          objectAttributeFilters: state.draft.objectAttributeFilters.map((filter, i) =>
            i === objIndex ? { ...filter, ...objUpdates } : filter
          ),
        },
      };

    case 'UPDATE_DRAFT_EVENT_ATTRIBUTE_FILTER_SETTINGS':
      return {
        ...state,
        draft: {
          ...state.draft,
          eventAttributeFilterSettings: { ...state.draft.eventAttributeFilterSettings, ...action.payload },
        },
      };

    case 'UPDATE_DRAFT_OBJECT_ATTRIBUTE_FILTER_SETTINGS':
      return {
        ...state,
        draft: {
          ...state.draft,
          objectAttributeFilterSettings: { ...state.draft.objectAttributeFilterSettings, ...action.payload },
        },
      };

    case 'APPLY_OBJECT_ATTRIBUTE_FILTER_CONFIG': {
      // Replaces the object attribute filter group and applies it immediately.
      // Based on state.active (not draft) so abandoned panel edits are never applied;
      // draft is overwritten too so the panel shows the applied state on next open.
      const { conditions, mode, matchType } = action.payload;
      const next = {
        ...state.active,
        objectAttributeFilters: conditions.map(c => ({ attribute: c.attribute, value: c.value, enabled: true })),
        objectAttributeFilterSettings: { mode, matchType },
      };
      return { ...state, active: next, draft: next };
    }

    case 'SET_VARIANT_FILTER':
      return {
        ...state,
        active: { ...state.active, variantFilter: { ...state.active.variantFilter, ...action.payload } },
        draft: { ...state.draft, variantFilter: { ...state.draft.variantFilter, ...action.payload } },
      };

    case 'SET_EDGE_DURATION_FILTER_FROM_EDGE_CLICK': {
      const { edgeId, edgeLabel, scopeKind, objectType } = action.payload;
      const newFilter = {
        edgeId,
        edgeLabel,
        scopeKind,
        objectType: objectType || null,
        operator: 'le',
        value: null,
        unit: 'days',
        thresholdMs: null,
        mode: 'include',
      };
      return {
        ...state,
        draft: {
          ...state.draft,
          edgeDurationFilters: [newFilter],
        },
        // filterPanelOpen: true, // REMOVED: Don't auto-open panel in modal interaction pattern
      };
    }

    case 'UPDATE_DRAFT_EDGE_DURATION_FILTER': {
      const { updates } = action.payload;
      console.log('🔧 UPDATE_DRAFT_EDGE_DURATION_FILTER - Updates:', updates);
      const existing = state.draft.edgeDurationFilters?.[0] || null;
      console.log('🔧 UPDATE_DRAFT_EDGE_DURATION_FILTER - Existing filter:', existing);
      if (!existing) {
        console.log('🔧 UPDATE_DRAFT_EDGE_DURATION_FILTER - No existing filter, returning state unchanged');
        return state;
      }
      const updated = { ...existing, ...updates };
      console.log('🔧 UPDATE_DRAFT_EDGE_DURATION_FILTER - Updated filter:', updated);
      const newState = {
        ...state,
        draft: { ...state.draft, edgeDurationFilters: [updated] },
      };
      console.log('🔧 UPDATE_DRAFT_EDGE_DURATION_FILTER - New draft edgeDurationFilters:', newState.draft.edgeDurationFilters);
      return newState;
    }

    case 'SET_DRAFT_OBJECT_ATTRIBUTE_PRESENCE_FILTERS':
      console.log('🔧 SET_DRAFT_OBJECT_ATTRIBUTE_PRESENCE_FILTERS:', action.payload);
      const newState = {
        ...state,
        draft: { ...state.draft, objectAttributePresenceFilters: { ...state.draft.objectAttributePresenceFilters, ...action.payload } },
      };
      console.log('🔧 New draft objectAttributePresenceFilters:', newState.draft.objectAttributePresenceFilters);
      return newState;

    case 'TOGGLE_DRAFT_OBJECT_ATTRIBUTE_NAME':
      const { attributeName } = action.payload;
      const isAttributeSelected = state.draft.objectAttributePresenceFilters.attributeNames.includes(attributeName);
      console.log('🔧 TOGGLE_DRAFT_OBJECT_ATTRIBUTE_NAME:', attributeName, 'currently selected:', isAttributeSelected);
      const newAttributeState = {
        ...state,
        draft: {
          ...state.draft,
          objectAttributePresenceFilters: {
            ...state.draft.objectAttributePresenceFilters,
            attributeNames: isAttributeSelected
              ? state.draft.objectAttributePresenceFilters.attributeNames.filter(name => name !== attributeName)
              : [...state.draft.objectAttributePresenceFilters.attributeNames, attributeName],
          },
        },
      };
      console.log('🔧 New attribute names:', newAttributeState.draft.objectAttributePresenceFilters.attributeNames);
      return newAttributeState;

    case 'TOGGLE_EDGE_SELECT_MODE':
      return {
        ...state,
        edgeSelectMode: !state.edgeSelectMode,
        edgeDurationMode: false,  // Deactivate duration mode when activating edge select mode
        draft: {
          ...state.draft,
          edgeDurationFilters: []  // Clear edge duration filter when switching to edge select mode
        }
      };

    case 'TOGGLE_EDGE_DURATION_MODE':
      return {
        ...state,
        edgeDurationMode: !state.edgeDurationMode,
        edgeSelectMode: false,  // Deactivate edge select mode when activating duration mode
        draft: {
          ...state.draft,
          edgeContainmentFilters: {
            ...state.draft.edgeContainmentFilters,
            edges: []  // Clear edge containment edges when switching to duration mode
          }
        }
      };

    case 'ADD_EDGE_TO_CONTAINMENT_FILTER': {
      const { sourceActivity, targetActivity, edgeLabel } = action.payload;

      // Check for duplicates
      const isDuplicate = state.draft.edgeContainmentFilters.edges.some(
        edge => edge.sourceActivity === sourceActivity && edge.targetActivity === targetActivity
      );

      if (isDuplicate) {
        console.log('🔗 Edge already selected:', edgeLabel);
        return state;
      }

      return {
        ...state,
        draft: {
          ...state.draft,
          edgeContainmentFilters: {
            ...state.draft.edgeContainmentFilters,
            edges: [
              ...state.draft.edgeContainmentFilters.edges,
              { sourceActivity, targetActivity, edgeLabel }
            ]
          }
        }
      };
    }

    case 'REMOVE_EDGE_FROM_CONTAINMENT_FILTER':
      return {
        ...state,
        draft: {
          ...state.draft,
          edgeContainmentFilters: {
            ...state.draft.edgeContainmentFilters,
            edges: state.draft.edgeContainmentFilters.edges.filter(
              (_, index) => index !== action.payload
            )
          }
        }
      };

    case 'UPDATE_EDGE_CONTAINMENT_CONFIG':
      return {
        ...state,
        draft: {
          ...state.draft,
          edgeContainmentFilters: {
            ...state.draft.edgeContainmentFilters,
            ...action.payload
          }
        }
      };

    case 'APPLY_EDGE_CONTAINMENT_FILTER': {
      // Only enable if there are actually edges selected
      const hasEdges = state.draft.edgeContainmentFilters.edges.length > 0;
      return {
        ...state,
        edgeSelectMode: false,
        draft: {
          ...state.draft,
          edgeContainmentFilters: {
            ...state.draft.edgeContainmentFilters,
            enabled: hasEdges  // Only enable if edges exist
          }
        }
      };
    }

    case 'APPLY_EDGE_DURATION_FILTER': {
      // Only keep edge duration filter if it has valid data
      const edgeDurationFilter = state.draft.edgeDurationFilters?.[0];
      const hasValidFilter = edgeDurationFilter &&
                           edgeDurationFilter.edgeId &&
                           edgeDurationFilter.value !== null &&
                           edgeDurationFilter.value !== '';

      return {
        ...state,
        edgeDurationMode: false,  // Turn off mode when applying filter
        draft: {
          ...state.draft,
          edgeDurationFilters: hasValidFilter ? state.draft.edgeDurationFilters : []  // Clear if invalid
        }
      };
    }

    case 'CLEAR_EDGE_DURATION_FILTER':
      return {
        ...state,
        draft: {
          ...state.draft,
          edgeDurationFilters: []
        }
      };

    case 'ADD_ACTIVITY_NODE': {
      const { activity } = action.payload;
      if (state.draft.activityNodeFilters.nodes.includes(activity)) {
        return state; // Prevent duplicates
      }
      return {
        ...state,
        draft: {
          ...state.draft,
          activityNodeFilters: {
            ...state.draft.activityNodeFilters,
            nodes: [...state.draft.activityNodeFilters.nodes, activity]
          }
        }
      };
    }

    case 'REMOVE_ACTIVITY_NODE':
      return {
        ...state,
        draft: {
          ...state.draft,
          activityNodeFilters: {
            ...state.draft.activityNodeFilters,
            nodes: state.draft.activityNodeFilters.nodes.filter((_, i) => i !== action.payload.index)
          }
        }
      };

    case 'UPDATE_ACTIVITY_NODE_CONFIG':
      return {
        ...state,
        draft: {
          ...state.draft,
          activityNodeFilters: {
            ...state.draft.activityNodeFilters,
            ...action.payload
          }
        }
      };

    case 'APPLY_ACTIVITY_NODE_FILTER':
      return {
        ...state,
        draft: {
          ...state.draft,
          activityNodeFilters: {
            ...state.draft.activityNodeFilters,
            enabled: true
          }
        }
      };

    case 'APPLY_FILTERS':
      console.log('🚀 APPLY_FILTERS - Draft state being applied:', state.draft);
      console.log('🚀 APPLY_FILTERS - Draft eventAttributeFilters:', state.draft.eventAttributeFilters);
      console.log('🚀 APPLY_FILTERS - Object attribute presence filters:', state.draft.objectAttributePresenceFilters);
      console.log('🚀 APPLY_FILTERS - Edge duration filters:', state.draft.edgeDurationFilters);
      const appliedState = { ...state, active: { ...state.draft }, filterPanelOpen: false };
      console.log('🚀 APPLY_FILTERS - New active state:', appliedState.active);
      console.log('🚀 APPLY_FILTERS - New active eventAttributeFilters:', appliedState.active.eventAttributeFilters);
      console.log('🚀 APPLY_FILTERS - New active edge duration filters:', appliedState.active.edgeDurationFilters);
      return appliedState;

    case 'RESET_DRAFT':
      return { ...state, draft: { ...state.active } };

    case 'CLEAR_FILTERS':
      const clearedState = {
        fileType: state.active.fileType,
        objectTypes: [],
        dateRange: { startDate: null, endDate: null, enabled: false },
        activityFilters: { included: [], excluded: [], enabled: false },
        leadTimeRange: { min: null, max: null, enabled: false },
        objectAttributePresenceFilters: { attributeNames: [], enabled: false },
        eventAttributeFilters: [],
        eventAttributeFilterSettings: { matchType: 'all' },
        objectAttributeFilters: [],
        objectAttributeFilterSettings: { mode: 'include', matchType: 'all' },
        variantFilter: { objectType: null, variantActivitiesList: null, enabled: false, displayMode: 'fade' },
        edgeDurationFilters: [],
        edgeContainmentFilters: { enabled: false, edges: [], matchType: 'any', mode: 'include' },
        activityNodeFilters: { enabled: false, nodes: [], matchType: 'any', mode: 'include' },
      };
      return { ...state, active: clearedState, draft: clearedState, edgeSelectMode: false, edgeDurationMode: false };

    case 'TOGGLE_FILTER_PANEL':
      if (!state.filterPanelOpen) {
        return { ...state, draft: { ...state.active }, filterPanelOpen: true };
      }
      return { ...state, filterPanelOpen: false };

    default:
      return state;
  }
};

const GlobalFilterContext = createContext();

export const GlobalFilterProvider = ({ children }) => {
  const [state, dispatch] = useReducer(filterReducer, initialFilterState);
  const [availableActivities, setAvailableActivities] = React.useState([]);
  const ocelContext = useOcelContext();



  const filterSummary = useMemo(() => {
    const activeFilters = [];
    const { active } = state;

    if (active.objectTypes && active.objectTypes.length > 0) {
      activeFilters.push(`Object Types (${active.objectTypes.length})`);
    }
    if (active.dateRange.enabled && (active.dateRange.startDate || active.dateRange.endDate)) {
      activeFilters.push('Date Range');
    }
    if (active.eventAttributeFilters.some(f => f.enabled)) {
      activeFilters.push('Event Attributes');
    }
    if (active.activityFilters.enabled && (active.activityFilters.included.length > 0 || active.activityFilters.excluded.length > 0)) {
      activeFilters.push('Activities');
    }
    if (active.leadTimeRange.enabled && (active.leadTimeRange.min !== null || active.leadTimeRange.max !== null)) {
      activeFilters.push('Lead Time');
    }
    if (active.objectAttributePresenceFilters.enabled && active.objectAttributePresenceFilters.attributeNames.length > 0) {
      activeFilters.push('Object Attribute Presence');
    }
    if (active.objectAttributeFilters.some(f => f.enabled)) {
      activeFilters.push('Object Attribute Values');
    }
    if (active.variantFilter.enabled && active.variantFilter.variantActivitiesList && active.variantFilter.variantActivitiesList.length > 0) {
      activeFilters.push(`Variant (${active.variantFilter.variantActivitiesList.length})`);
    }
    // Edge duration filters - check for complete filters with all required fields
    const completeEdgeFilters = (active.edgeDurationFilters || []).filter(
      f => f && f.edgeId && f.scopeKind && f.operator && typeof f.thresholdMs === 'number' && f.mode
    );
    if (completeEdgeFilters.length > 0) {
      activeFilters.push(`Edge Duration (${completeEdgeFilters.length})`);
    }
    // Edge containment filters
    if (active.edgeContainmentFilters.enabled && active.edgeContainmentFilters.edges.length > 0) {
      activeFilters.push(`Edge Containment (${active.edgeContainmentFilters.edges.length})`);
    }
    // Activity node filters
    if (active.activityNodeFilters.enabled && active.activityNodeFilters.nodes.length > 0) {
      activeFilters.push(`Activity Nodes (${active.activityNodeFilters.nodes.length})`);
    }

    return {
      count: activeFilters.length,
      types: activeFilters,
      hasFilters: activeFilters.length > 0,
    };
  }, [state.active]);

  const value = {
    filterState: state.active,
    draftState: state.draft,
    filterPanelOpen: state.filterPanelOpen,
    edgeSelectMode: state.edgeSelectMode,
    edgeDurationMode: state.edgeDurationMode,
    dispatch,
    filterSummary,
    toggleFilterPanel: () => dispatch({ type: 'TOGGLE_FILTER_PANEL' }),
    availableActivities,
  };

  // Debug log the current filter state
  React.useEffect(() => {
    console.log('🎯 GlobalFilterContext - Active filter state changed:', state.active);
    console.log('🎯 GlobalFilterContext - Object attribute presence filters:', state.active.objectAttributePresenceFilters);
  }, [state.active]);

  // Dispatch event when modes change to clear edge highlighting
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('edge-mode-changed', {
      detail: { edgeSelectMode: state.edgeSelectMode, edgeDurationMode: state.edgeDurationMode }
    }));
  }, [state.edgeSelectMode, state.edgeDurationMode]);

  // Wire global event from graph edge click to create edge-duration filter
  useEffect(() => {
    const handler = (e) => {
      const { edgeId, edgeLabel, scopeKind, objectType } = e.detail || {};
      if (!edgeId) return;
      dispatch({
        type: 'SET_EDGE_DURATION_FILTER_FROM_EDGE_CLICK',
        payload: { edgeId, edgeLabel, scopeKind, objectType },
      });
    };
    window.addEventListener('edge-duration-filter:create', handler);
    return () => window.removeEventListener('edge-duration-filter:create', handler);
  }, []);

  // Wire global event from graph edge click to add edge to containment filter
  useEffect(() => {
    const handler = (e) => {
      const { sourceActivity, targetActivity, edgeLabel } = e.detail || {};
      if (!sourceActivity || !targetActivity) return;
      dispatch({
        type: 'ADD_EDGE_TO_CONTAINMENT_FILTER',
        payload: { sourceActivity, targetActivity, edgeLabel },
      });
    };
    window.addEventListener('edge-containment-filter:add-edge', handler);
    return () => window.removeEventListener('edge-containment-filter:add-edge', handler);
  }, []);

  // Sync available activities from OcelContext metadata (no API call needed)
  useEffect(() => {
    const activities = ocelContext?.metadata?.event_types;
    if (activities) {
      setAvailableActivities(activities);
      console.log('📋 Available activities synced from OcelContext metadata:', activities);
    }
  }, [ocelContext?.metadata]);

  return (
    <GlobalFilterContext.Provider value={value}>
      {children}
    </GlobalFilterContext.Provider>
  );
};

export const useGlobalFilters = () => {
  const context = useContext(GlobalFilterContext);
  if (!context) {
    throw new Error('useGlobalFilters must be used within a GlobalFilterProvider');
  }
  // Inside a tab with active tab-level filters, filterState is shadowed with
  // the tab's filters — dispatch/draftState/toggleFilterPanel/filterSummary
  // etc. still point at the true global reducer (the floating GlobalFilterPanel
  // is mounted outside any tab subtree, so it's never affected by this).
  const tabOverride = useTabFilterOverride();
  return tabOverride ? { ...context, filterState: tabOverride } : context;
};
